import { useEffect, useState } from 'react';
import { Spinner, Text } from '@lmn/design-system';
import styled from 'styled-components';
import { useTranslation } from '@/app/components/translations/Translation';

// A stay priced earlier in the session comes back from the SWR cache within a few frames; showing the
// panel (and the veil) for that would read as a flash. It only appears once the load has outlasted this.
const SHOW_DELAY_MS = 300;

// Shown over the rooms placeholders while the stay just picked in the apex calendar is priced (MPH-971).
// Two layers sit over the board-class bar and the skeleton cards, which keep their size underneath so
// nothing shifts when the panel comes and goes: a white veil that fades them, and the centred panel with
// the spinner and the message. Both are absolutely positioned, so the parent must be `position: relative`.

// 60% white over the placeholders, as designed — on the white page the same as fading them to 40%.
const Veil = styled.div`
  position: absolute;
  inset: 0;
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundPage};
  opacity: 0.6;
`;

// Centres the panel over the placeholders. Capped to one viewport height so the panel stays on screen when
// the skeleton column is taller than the viewport (three cards on a phone): centred in the block when it
// fits, which is the designed case, otherwise in the first screenful of it.
const Stage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: min(100%, 100vh);
  height: min(100%, 100dvh);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.kallisto.space.space100};
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.kallisto.space.space200};
  box-sizing: border-box;
  width: 230px;
  max-width: 100%;
  padding: ${({ theme }) => theme.kallisto.space.space200} ${({ theme }) => theme.kallisto.space.space300};
  border: 1px solid ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  border-radius: ${({ theme }) => theme.kallisto.border.radius.radius200};
  background: ${({ theme }) => theme.kallisto.color.background.static.backgroundLayer02};
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};
`;

export function EvoRoomsLoadingPanel() {
  const { t } = useTranslation();
  // The parent unmounts this component when the rooms land, so a load that finishes inside the delay
  // never shows anything.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  if (!visible) return null;

  return (
    <>
      <Veil aria-hidden="true" />
      <Stage>
        <Panel data-testid="evo-rooms-loading-panel">
          <Spinner variant="accent" size={32} />
          {/* The DS spinner's own status region is empty, so the message is the one announced. */}
          <Text variant="BodySmall" role="status" css="text-align: center;">
            {t('vacation.select.evo_price_calendar.loading_message')}
          </Text>
        </Panel>
      </Stage>
    </>
  );
}
